import { API_BASE_URL, ApiError } from './client';
import { ValidationFailure } from './events';
import type { FieldErrors } from './events';

/**
 * One line on a list. `assignedToPersonId` is the griddle question's answer — who is bringing it,
 * as state on the event rather than as a message somebody has to scroll back for.
 */
export interface PackingItem {
  id: string;
  text: string;
  category: string | null;
  quantity: number;
  sortOrder: number;
  isOptional: boolean;
  notes: string | null;
  assignedToPersonId: string | null;
  assignedToName: string | null;
  isChecked: boolean;
  checkedAt: string | null;
  /** FromTemplate | AddedLocally — what makes "what did I change" answerable. */
  origin: string;
  /**
   * This one is the troop's and it comes back. Carried on every list a reader can already see,
   * a Scout's own and their parent's view of it included — the badge is how a family finds out the
   * troop's sleeping bag is in their car. It discloses nothing: they were already entitled to this
   * list.
   */
  isUnitOwned: boolean;
  /**
   * Returned | Damaged | Missing, or null while nobody has said. Only ever set on a troop-owned
   * line, and never folded into a boolean — *back with a broken zip* and *nobody has looked yet*
   * are different answers.
   */
  returned: string | null;
}

/**
 * A packing list, template or live — they are the same shape, which is what makes every operation
 * in §8 a copy in one direction or another.
 *
 * `ownerPersonId` set means it belongs to a person rather than to a unit, and is **global**: a
 * Scout's own list follows them to every unit they belong to.
 */
export interface PackingList {
  id: string;
  /** Template | EventList */
  kind: string;
  /** Unit | Subgroup | Personal — the scope of gear it is about, not who owns it. */
  level: string;
  name: string;
  eventId: string | null;
  ownerUnitId: string | null;
  ownerSubgroupId: string | null;
  ownerPersonId: string | null;
  subgroupId: string | null;
  /** Whose working copy this is, on a personal list. Null is the published suggestion. */
  forPersonId: string | null;
  /** What it was copied from. Lineage, never authority. */
  sourceListId: string | null;
  /**
   * The kind of event this saved list is suggested for, or null. Templates only, and never on your
   * own lists — a kind belongs to one unit and your lists follow you between units.
   */
  eventTypeId: string | null;
  /** Whether this caller may change it — asked per list, because it genuinely differs per list. */
  canWrite: boolean;
  checkedCount: number;
  items: PackingItem[];
}

/** One list a kind of event suggests, and whether this event already carries a copy. */
export interface PackingOffer {
  sourceListId: string;
  name: string;
  level: string;
  itemCount: number;
  alreadyPublished: boolean;
}

/** What a leader sees about personal packing: counts, and never contents. */
export interface PackingProgress {
  expected: number;
  started: number;
  finished: number;
}

async function call<T>(path: string, init?: RequestInit): Promise<T> {
  const response = await fetch(`${API_BASE_URL}${path}`, {
    credentials: 'include',
    headers: { 'Content-Type': 'application/json', ...init?.headers },
    ...init,
  });

  if (!response.ok) {
    let problem: { title?: string; detail?: string; errors?: FieldErrors } = {};
    try {
      problem = await response.json();
    } catch {
      // Non-JSON body; the status is enough.
    }

    if (response.status === 400 && problem.errors) {
      throw new ValidationFailure(problem.errors);
    }

    throw new ApiError(
      response.status,
      problem.detail ?? problem.title ?? `Failed (${response.status}).`,
    );
  }

  return response.status === 204 ? (undefined as T) : ((await response.json()) as T);
}

export const packingApi = {
  /** The unit's saved lists plus this person's own, which is what they can copy from. */
  templates: (unitId: string) => call<PackingList[]>(`/units/${unitId}/packing-templates/`),

  saveForUnit: (unitId: string, input: { name: string; level: string; ownerSubgroupId?: string | null }) =>
    call<PackingList>(`/units/${unitId}/packing-templates/`, {
      method: 'POST',
      body: JSON.stringify(input),
    }),

  /**
   * A person's own lists. Not under `/units/` on purpose: they belong to no unit, and asking for
   * them under one would be asking the wrong question.
   */
  mine: () => call<PackingList[]>('/me/packing-templates/'),

  saveForMe: (name: string) =>
    call<PackingList>('/me/packing-templates/', {
      method: 'POST',
      body: JSON.stringify({ name, level: 'Personal' }),
    }),

  /** What an event offers, already filtered to what this caller may see. */
  forEvent: (unitId: string, eventId: string) =>
    call<PackingList[]>(`/units/${unitId}/events/${eventId}/packing/`),

  /** Officer and above. Counts only — the contents are nobody else's business (§8). */
  progress: (unitId: string, eventId: string) =>
    call<PackingProgress>(`/units/${unitId}/events/${eventId}/packing/progress`),

  /**
   * What the event's own kind suggests: the type's saved lists plus the ones the type it refines
   * suggests, deduplicated by name with the child winning. Officer and above.
   */
  offers: (unitId: string, eventId: string) =>
    call<PackingOffer[]>(`/units/${unitId}/events/${eventId}/packing/offers`),

  /** Files a saved list under a kind of event, or takes it out from under one. */
  suggestForType: (unitId: string, listId: string, eventTypeId: string | null) =>
    call<PackingList>(`/units/${unitId}/packing-lists/${listId}/event-type`, {
      method: 'PUT',
      body: JSON.stringify({ eventTypeId }),
    }),

  publish: (unitId: string, eventId: string, sourceListId: string) =>
    call<PackingList>(`/units/${unitId}/events/${eventId}/packing/`, {
      method: 'POST',
      body: JSON.stringify({ sourceListId }),
    }),

  /**
   * One subgroup's working copy of the event's subgroup-level suggestion. Idempotent, so this is
   * also how you open the copy that already exists — one suggestion, N copies, and every group
   * assigns its own members without stepping on the others.
   */
  adoptForSubgroup: (unitId: string, eventId: string, subgroupId: string) =>
    call<PackingList>(
      `/units/${unitId}/events/${eventId}/packing/subgroups/${subgroupId}`,
      { method: 'POST' },
    ),

  /** Undefined when they have not started one — nothing exists until they do (§8, N2). */
  myList: (unitId: string, eventId: string) =>
    call<PackingList | undefined>(`/units/${unitId}/events/${eventId}/packing/mine`),

  startMyList: (unitId: string, eventId: string, from: string, templateId?: string) =>
    call<PackingList>(`/units/${unitId}/events/${eventId}/packing/mine`, {
      method: 'POST',
      body: JSON.stringify({ from, templateId: templateId ?? null }),
    }),

  list: (unitId: string, listId: string) =>
    call<PackingList>(`/units/${unitId}/packing-lists/${listId}/`),

  addItem: (
    unitId: string,
    listId: string,
    input: { text: string; category?: string | null; quantity?: number; isOptional?: boolean },
  ) =>
    call<PackingList>(`/units/${unitId}/packing-lists/${listId}/items`, {
      method: 'POST',
      body: JSON.stringify(input),
    }),

  removeItem: (unitId: string, listId: string, itemId: string) =>
    call<PackingList>(`/units/${unitId}/packing-lists/${listId}/items/${itemId}`, {
      method: 'DELETE',
    }),

  check: (unitId: string, listId: string, itemId: string, isChecked: boolean) =>
    call<PackingList>(`/units/${unitId}/packing-lists/${listId}/items/${itemId}/checked`, {
      method: 'PUT',
      body: JSON.stringify({ isChecked }),
    }),

  /** Officer act at every level it exists at — and that includes the youth Patrol Leader. */
  assign: (unitId: string, listId: string, itemId: string, personId: string | null) =>
    call<PackingList>(`/units/${unitId}/packing-lists/${listId}/items/${itemId}/assignee`, {
      method: 'PUT',
      body: JSON.stringify({ personId }),
    }),

  saveAsTemplate: (
    unitId: string,
    listId: string,
    input: { name: string; owner: 'me' | 'unit'; ownerSubgroupId?: string | null },
  ) =>
    call<PackingList>(`/units/${unitId}/packing-lists/${listId}/save-as-template`, {
      method: 'POST',
      body: JSON.stringify(input),
    }),
};

/**
 * The same feature through the guardian's door.
 *
 * A parent holds no membership, so `UnitScopeMiddleware` 404s them on every `/units/` route before
 * an endpoint runs -- these exist for that reason and not as a convenience. The rules are the same
 * ones: `FamilyPackingEndpoints` reaches `PackingAccessPolicy`, which names the guardian, so there
 * is no second opinion here about who may read or write what.
 */
export const familyPackingApi = {
  /**
   * Everything one event is asking of this family in a single request: the unit's lists, which a
   * guardian reads by standing, plus each of their own children's working copies. A patrol's list
   * is deliberately absent -- the policy gives a guardian who is not in the group `None`.
   */
  forEvent: (eventId: string) => call<PackingList[]>(`/family/events/${eventId}/packing`),

  /** Starting a child's list for them, which is the whole point: a Scout with no phone never does. */
  startForChild: (eventId: string, childId: string, from: string, templateId?: string) =>
    call<PackingList>(`/family/events/${eventId}/packing/children/${childId}`, {
      method: 'POST',
      body: JSON.stringify({ start: from, templateId: templateId ?? null }),
    }),

  /** Ticking on a child's behalf. The row records that the parent did it, not the child. */
  check: (listId: string, itemId: string, isChecked: boolean) =>
    call<PackingList>(`/family/packing-lists/${listId}/items/${itemId}/checked`, {
      method: 'PUT',
      body: JSON.stringify({ isChecked }),
    }),
};
